import * as $ from "jquery";
import onThrottled from "../../utils/event";
import imkt from '../../imkt';
import general from '../../utils/general';

type ModalParams = {
  modalType?: string;
  contentClickToHide?: boolean;
  fixed?: boolean;
  centerVertically?: boolean;
  modalContainer?: any;
  id?: any;
  onHide?: () => void;
};

export default class Modal {
  aboveClass = "above";
  params = {
    // define default params here
    modalType: "dialog",
    centerVertically: true,
    modalContainer: document.body,
    fixed: true,
    id: general.randomString(8),
    contentClickToHide: true,
    onHide: null,
  };

  $overlay?: JQuery<HTMLElement>;
  $overlayContainer: any;
  $modal?: JQuery<HTMLElement>;
  $modalHeader?: JQuery<HTMLElement>;
  $modalContent?: JQuery<HTMLElement>;
  $closeLink?: JQuery<HTMLElement>;
  isVisible: any;
  inserted: any;

  constructor(params: ModalParams) {
    this.params = {
      ...this.params,
      ...params,
    };

    this.$overlay = this.build();
    this.$overlayContainer = this.params.fixed
      ? $(window)
      : this.$overlay.parent();
    this.$modal = $(".modal", this.$overlay).addClass(this.params.modalType);
    this.$modalHeader = $(".modal-header", this.$modal);
    this.$modalContent = $(".modal-content", this.$modal);
    this.$closeLink = $(".close", this.$modal);
  }

  init() {
  }

  centerModal() {
    var self = this;
    this.$modal?.css({
      margin: "0 auto",
      top() {
        return Math.max(
          (self.$overlayContainer.outerHeight() - $(this).outerHeight()) / 2,
          0
        );
      },
    });
  }

  bindModalEvents() {
    onThrottled($(window),
      "resize." + this.params.id,
      $.proxy(function () {
        this.place();
      }, this)
    );

    onThrottled($(window),
      "scroll." + this.params.id,
      $.proxy(function () {
        this.place();
      }, this)
    );

    $(document).on(
      "keyup." + this.params.id,
      $.proxy(function (e) {
        if (e.which === 27) {
          this.hide();
        }
      }, this)
    );

    this.$overlay.on(
      "click." + this.params.id,
      $.proxy(function (e) {
        if (this.isVisible) {
          // Allow parameter to hide modal on modal content click (pre-existing behavior)
          var isContentClick = $(e.target).closest(".modal-content").length;

          if (
            this.params.contentClickToHide ||
            (!this.params.contentClickToHide && !isContentClick)
          ) {
            this.hide();
          }
        }
      }, this)
    );

    this.$modal.on(
      "click",
      "[data-dismiss='modal']",
      $.proxy(function (e) {
        e.preventDefault();
        this.hide();
      }, this)
    );

    $(document).on(
      "modal.shown",
      function () {
        setTimeout(
          function () {
            this.$closeLink.focus();
          }.bind(this),
          100
        );
      }.bind(this)
    );

    $("iframe, img", this.$modal).on(
      "load",
      function (e) {
        if ($(e.currentTarget).is("iframe")) {
          setTimeout(this.place.bind(this), 100);
        } else {
          this.place();
        }
      }.bind(this)
    );
  }

  unbindModalEvents() {
    $(window).off("resize." + this.params.id);
    $(document).off("keyup." + this.params.id);
  }

  build() {
    const markup = `
        <div class="overlay" data-foo="bar">
          <div class="modal" tabindex="-1" role="dialog" aria-labelledby="modal-header">
            <a href="#" class="close aui-icon aui-icon-large aui-iconfont-remove-label" data-dismiss="modal" title="Close overlay (escape key)"></a>
            <div class="modal-header" id="modal-header"></div>
            <div class="modal-content"></div>
          </div>
        </div>
      `;

    const $overlay = $(markup);
    $overlay.attr("id", this.params.id);

    return $overlay;
  }

  show() {
    if (!this.isVisible) {
      $(this.params.modalContainer).addClass("modal-open");
      this.insert();
      this.$overlay
        ?.addClass("in")
        .trigger("modal.show")
        .delay(200)
        .trigger("modal.shown");
      this.isVisible = true;
      this.place();

      // Exception we make for modals that contain ExactTarget forms; rebind the form events
      var exactTargetForm = this.$modal?.find(".exact-target-form__wrapper");
      if (exactTargetForm?.length) {
        new imkt.components.ExactTargetFormCommonV2(exactTargetForm); // pass in domRoot
      }

      // Initialize the components inside the modal
      imkt.core.init(this.$modal);
    }
  }

  hide() {
    if (this.isVisible) {
      this.$overlay
        ?.removeClass("in")
        .trigger("modal.hide")
        .delay(200)
        .trigger("modal.hidden");
      this.isVisible = false;
      $(this.params.modalContainer).removeClass("modal-open");

      if (this.params.onHide && typeof this.params.onHide == "function") {
        this.params.onHide();
      }
    }
  }

  place() {
    var unFilledImages = this.$modal[0].querySelectorAll("img[data-src]");
    for (var i = 0; i < unFilledImages.length; i++) {
      unFilledImages[i].src = unFilledImages[i].getAttribute("data-src");
      unFilledImages[i].removeAttribute("data-src");
    }

    var closeLinkIsOutOfBounds, isRoomForCloseLink;

    if (this.params.fixed) {
      this.$overlay.css({
        width: $(window).width(),
        height: window.innerHeight ? window.innerHeight : $(window).height(),
      });
    }

    if (this.params.centerVertically) {
      this.centerModal();
    }

    // moves the close link around based on the available space
    closeLinkIsOutOfBounds =
      this.$closeLink.offset().left + this.$closeLink.outerWidth() >
      this.$overlay.offset().left + this.$overlay.outerWidth();
    isRoomForCloseLink =
      this.$overlay.outerWidth() -
      (this.$modal.offset().left + this.$modal.outerWidth()) >
      this.$closeLink.outerWidth();

    if (!this.$closeLink.hasClass(this.aboveClass) && closeLinkIsOutOfBounds) {
      this.$closeLink.addClass(this.aboveClass);
    } else if (isRoomForCloseLink) {
      this.$closeLink.removeClass(this.aboveClass);
    }

    this.$overlay.trigger("modal.place");
  }

  insert() {
    if (!this.inserted) {
      this.$overlay
        ?.appendTo(this.params.modalContainer)
        .trigger("modal.insert");
      this.bindModalEvents();
      this.inserted = true;
    }
  }

  remove() {
    this.$overlay?.trigger("modal.remove").remove();
    this.unbindModalEvents();
    this.inserted = false;
  }
}
